import { H2 } from "src/util/Typography";
import Table from "../ui/Table";
import { useEffect, useState } from "react";
import { useDateRange } from "src/context/DateRangeContext";
import reportApi from "./ReportApi";
import { useUser } from "src/context/UserContext";
import Loading from "../ui/Loading";
import {
  DateRange,
  checkDateRange,
  getLast7DaysRange,
  handlegetTransactionStartDate,
} from "src/util/Util";
import { StorageKey } from "src/util/Storage";
import { RenderIcon } from "src/assets/icons";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

const convertTextToCSV = (text: string) => {
  const rows = text.split("\n").map((row) => row.trim());
  if (rows.length === 0) return "";

  const header = rows[0].split(",");
  const publisherIndex = header.indexOf("Publisher");

  return rows
    .map((row, index) => {
      const columns = row.split(",");
      if (index === 0) {
        columns.splice(publisherIndex, 1);
      } else {
        columns.splice(0, 1);
      }
      return columns.join(",");
    })
    .join("\n");
};

const COLUMN_ORDER = [
  "Date",
  "Domain",
  "MobileAppId",
  "Ad-Request",
  "Ad-Response",
  "Impressions",
  "Fill Rate (%)",
  "Win Rate",
  "Gross Revenue ($)",
  "Commission ($)",
  "Net Revenue ($)",
  "eCPM ($)",
  "MediaType",
];

const COLUMN_RENAMES: Record<string, string> = {
  "Revenue ($)": "Gross Revenue ($)",
  "AlkimiCommission ($)": "Commission ($)",
};

const reorderCSV = (csv: string): string => {
  const lines = csv.trim().split("\n");
  if (lines.length === 0) return csv;

  const parseRow = (row: string): string[] => {
    const result: string[] = [];
    let current = "";
    let inQuotes = false;
    for (let i = 0; i < row.length; i++) {
      const char = row[i];
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === "," && !inQuotes) {
        result.push(current.trim());
        current = "";
      } else {
        current += char;
      }
    }
    result.push(current.trim());
    return result;
  };

  const originalHeaders = parseRow(lines[0]);
  const renamedHeaders = originalHeaders.map(
    (h) => COLUMN_RENAMES[h] || h
  );

  // Build index mapping based on desired order
  const orderedIndices = COLUMN_ORDER
    .map((col) => renamedHeaders.indexOf(col))
    .filter((i) => i !== -1);

  return lines
    .map((line, lineIndex) => {
      const cols = parseRow(line);
      const reordered = orderedIndices.map((i) =>
        lineIndex === 0 ? (COLUMN_RENAMES[cols[i]] || cols[i]) : cols[i]
      );
      return reordered.join(",");
    })
    .join("\n");
};

export const ReportsDashboard = () => {
  const { user } = useUser();
  const { dateRange, setDateRange, dashboardReportData, topN, setTopN, selectedDomains, selectedMobileAppIds } = useDateRange();
  const [reportDateRange, setReportDateRange] = useState<DateRange | any>(
    dateRange
  );
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [tableData, setTableData] = useState<string>("");
  const [openCustom, setOpenCustom] = useState<boolean>(
    dateRange?.fromDate ? true : false
  );

  const loadData = async () => {
    setIsOpen(false);
    setDateRange(reportDateRange);
    setOpenCustom(true);
    setLoading(true);
    const initialRange = getLast7DaysRange();

    const uuid = await reportApi
      .createPublisherDashboard({
        publisherId: user?.publisherId,
        fromDate: reportDateRange?.fromDate || initialRange.fromDate,
        toDate: reportDateRange?.toDate || initialRange.toDate,
        // domains:[ "prebid.alkimi-onboarding-dev.com",
        // "main.dq6ip3w73qwme.amplifyapp.com"]
      })
      .then((resp) => resp.data);
    if (!uuid) {
      return;
    }
    let interval = setInterval(async () => {
      try {
        const response = await reportApi.get(uuid ?? "");
        clearInterval(interval);
        const jsonx = await response.text();
        setTableData(reorderCSV(jsonx));
        setLoading(false);
      } catch (response) {
        if (typeof response === "object") {
          if (response.status !== 425) {
            setLoading(false);
            clearInterval(interval);
          }
        }
      }
    }, 500);
  };

  const initialLoad = () => {
    // loadData();
  };

  useEffect(() => {
    initialLoad();
    setReportDateRange(dateRange);
    if (!localStorage.getItem(StorageKey.DisabledDate)) {
      handlegetTransactionStartDate(user?.publisherId);
    }
  }, []);
  useEffect(() => {
    // console.log("dashboardReportData",dashboardReportData)
    if (dashboardReportData) {
      setTableData(reorderCSV(dashboardReportData));
    }
  }, [dashboardReportData]);
  const downloadCSV = (csvContent: string, suffix?: string) => {
    const publisherName = user?.username;
    const startDate = dateRange?.fromDate;
    const endDate = dateRange?.toDate;
    const baseName = `${publisherName}-Alkimi-Report`;
    const fileName = `${baseName}${suffix ? `-${suffix}` : ""}-${startDate}-${endDate}.csv`;
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", fileName);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const pollReport = (uuid: string): Promise<string> => {
    return new Promise((resolve, reject) => {
      const interval = setInterval(async () => {
        try {
          const response = await reportApi.get(uuid);
          clearInterval(interval);
          const csvText = await response.text();
          resolve(csvText);
        } catch (response: any) {
          if (typeof response === "object" && response.status !== 425) {
            clearInterval(interval);
            reject(response);
          }
        }
      }, 500);
    });
  };

  const parseCSVLine = (line: string): string[] => {
    const cols: string[] = [];
    let current = "";
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        // Handle escaped quotes ("")
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === "," && !inQuotes) {
        cols.push(current);
        current = "";
      } else {
        current += char;
      }
    }
    cols.push(current);
    return cols;
  };

  const serializeCSVField = (field: string): string => {
    // Re-quote fields that contain commas, quotes, or newlines
    if (field.includes(",") || field.includes('"') || field.includes("\n")) {
      return `"${field.replace(/"/g, '""')}"`;
    }
    return field;
  };

  const handleDownload = async () => {
    setLoading(true);
    try {
      const initialRange = getLast7DaysRange();

      const uuid = await reportApi
        .createPublisherDashboard({
          publisherId: user?.publisherId,
          fromDate: dateRange?.fromDate || initialRange.fromDate,
          toDate: dateRange?.toDate || initialRange.toDate,
          domains: ["all"],
          mobileAppIds: ["all"],
        })
        .then((resp: any) => resp.data);

      if (!uuid) {
        setLoading(false);
        return;
      }

      const csvText = await pollReport(uuid);
      const processedCSV = reorderCSV(csvText);
      if (processedCSV.trim()) {
        downloadCSV(processedCSV, "All");
      }
      setLoading(false);
    } catch (error) {
      console.error("Error downloading report:", error);
      setLoading(false);
    }
  };

  const handleLoadData = async (dateRange: any, state?: string) => {
    setDateRange(dateRange);
    setReportDateRange(dateRange);

    if (state === "Custom") {
      setReportDateRange({ fromDate: "", toDate: "" });

      return setIsOpen(true);
    }
    setLoading(true);
    const initialRange = getLast7DaysRange();

    const uuid = await reportApi
      .createPublisherDashboard({
        publisherId: user?.publisherId,
        fromDate: dateRange?.fromDate || initialRange.fromDate,
        toDate: dateRange?.toDate || initialRange.toDate,
      })
      .then((resp) => resp.data);
    if (!uuid) {
      return;
    }
    let interval = setInterval(async () => {
      try {
        const response = await reportApi.get(uuid ?? "");
        clearInterval(interval);
        const jsonx = await response.text();
        setTableData(reorderCSV(jsonx));
        setLoading(false);
      } catch (response) {
        if (typeof response === "object") {
          if (response.status !== 425) {
            setLoading(false);
            clearInterval(interval);
          }
        }
      }
    }, 500);
  };
  const handleClose = () => {
    setIsOpen(false);
  };
  const handleOpenDaypicker = () => {
    setIsOpen(true);
  };
  useEffect(() => {
    if (checkDateRange(dateRange?.fromDate) === "Custom") {
      setReportDateRange(dateRange);
    }
  }, [isOpen]);
  return (
    <>
      {" "}
      <main className="">
        {" "}
        {loading && <Loading />}
        <div className="grid">
          <div className="sticky top-[96px] z-10 bg-[#0A0A0A] pt-6 pb-4">
              <div className="w-[100%] flex items-center justify-between">
                <div className="flex items-center gap-16">
                  <H2 className="uppercase m-0">Report</H2>
                  <Select
                    value={String(topN)}
                    onValueChange={(value) => setTopN(Number(value))}
                  >
                    <SelectTrigger className="w-max px-0 text-[13px] leading-[18.2px] font-normal font-popins sm:text-[16px] sm:leading-[22.4px] bg-transparent border-none">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="w-[100%] font-popins text-[14px]">
                      {[
                        { value: "50", label: "Top 50" },
                        { value: "100", label: "Top 100" },
                      ].map((option) => (
                        <SelectItem key={option.value} value={option.value} className="text-[13px] sm:text-[14px] font-popins">
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-3">
                  <div
                    className="align-center cursor-pointer font-popins flex gap-2 items-center bg-[#111111] px-4 py-2 rounded-full"
                    onClick={() => handleDownload()}
                  >
                    <RenderIcon type="downLoadIcon" />
                    Download All Report
                  </div>
                </div>
              </div>
              <p className="hidden sm:block text-gray-60 font-popins text-[14px] mt-1 sm:w-[40%]">
                This view displays the highest-performing entries for quick insights. Download the complete report to access all data.
              </p>
          </div>
          {tableData && (
            <Table data={tableData} handleAction={handleOpenDaypicker} />
          )}

        </div>
      </main>
    </>
  );
};
